import React from "react";
import styled from "styled-components";
import { InputLabel } from "./Input";
import styles from "styles/styles";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";

const FileUploadContainer = styled.div`
    margin: 1rem 0;
`;

const FileInput = styled.input`
    display: none;
`;

const FileUploadButton = styled.button<{ isDragging?: boolean }>`
    background-color: ${(props) => (props.isDragging ? styles.colors.grey2 : styles.colors.white)};
    border: 1px solid
        ${(props) => (props.isDragging ? styles.colors.focusBorderColor : styles.colors.grey6)};
    border-radius: 0px;
    padding: 1rem;
    width: 100%;
    cursor: pointer;
    text-align: center;
    color: ${(props) => (props.isDragging ? "#007bff" : "#666")};
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        border-color: ${styles.colors.focusBorderColor};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const AttachmentsList = styled.div`
    margin-top: 0.5rem;
`;

const AttachmentItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
    margin: 0.25rem 0;
    background-color: #f8f9fa;
    border-radius: 4px;
    font-size: 14px;
`;

const AttachmentName = styled.span`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AttachmentSize = styled.span`
    margin-left: 0.5rem;
    color: #666;
    font-size: 12px;
`;

const RemoveAttachmentButton = styled.button`
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 0.25rem;
    margin-left: 0.5rem;
    font-size: 14px;

    &:hover {
        color: #c82333;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

interface FileUploadProps {
    attachments: File[];
    onAttachmentsChange: (files: File[]) => void;
    disabled?: boolean;
    accept?: string;
    label?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
    attachments,
    onAttachmentsChange,
    disabled = false,
    accept = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.svg",
    label,
}) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const translate = useTranslate();

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (files) {
            const newFiles = Array.from(files);
            onAttachmentsChange([...attachments, ...newFiles]);
        }

        event.target.value = "";
    };

    const handleRemoveAttachment = (indexToRemove: number) => {
        onAttachmentsChange(attachments.filter((_, index) => index !== indexToRemove));
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) {
            return "0 Bytes";
        }
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setIsDragging(false);

        const files = event.dataTransfer.files;

        if (files) {
            const newFiles = Array.from(files);
            onAttachmentsChange([...attachments, ...newFiles]);
        }
    };

    const fileUploadId = React.useId();

    return (
        <div>
            <FileUploadContainer>
                <InputLabel>{label}</InputLabel>
                <FileInput
                    id={fileUploadId}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    disabled={disabled}
                    accept={accept}
                />
                <FileUploadButton
                    type="button"
                    onClick={() => document.getElementById(fileUploadId)?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    isDragging={isDragging}
                    disabled={disabled}
                >
                    {isDragging ? (
                        translate("featureRequests.featureRequestFileUpload.dropFilesHere")
                    ) : (
                        <>
                            {translate(
                                "featureRequests.featureRequestFileUpload.selectOrDragFiles"
                            )}
                            <br />
                            <small>
                                {translate(
                                    "featureRequests.featureRequestFileUpload.supportedFileTypes"
                                )}
                            </small>
                        </>
                    )}
                </FileUploadButton>
                {attachments.length > 0 && (
                    <AttachmentsList>
                        {attachments.map((file, index) => (
                            <AttachmentItem key={`${file.name}-${index}`}>
                                <AttachmentName title={file.name}>{file.name}</AttachmentName>
                                <AttachmentSize>{formatFileSize(file.size)}</AttachmentSize>
                                <RemoveAttachmentButton
                                    type="button"
                                    onClick={() => handleRemoveAttachment(index)}
                                    disabled={disabled}
                                    title={translate(
                                        "featureRequests.featureRequestFileUpload.removeAttachment"
                                    )}
                                >
                                    ✕
                                </RemoveAttachmentButton>
                            </AttachmentItem>
                        ))}
                    </AttachmentsList>
                )}
            </FileUploadContainer>
        </div>
    );
};

export default FileUpload;
